// ============================================================================
// ADDONTABS.JS - Barra de pestañas de módulos/addons del panel
// ============================================================================
// Vive entre la cabecera (Layout) y el contenido. Renderiza SIEMPRE la pestaña
// base "Tareas" + una pestaña por addon ACTIVO del tenant (Crawl SEO, Google
// Ads). Los addons activos llegan por prop desde Layout, que los lee de la
// config del cliente ya cargada (get_public_config → data.addons).
//
// La pestaña activa se deriva de la ruta actual (useLocation). La activación de
// módulos NO se hace desde aquí: es admin-only y se gestiona desde Configuración
// de cliente → Módulos.
// ============================================================================

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListChecks, Globe, History, Search, Megaphone, Sparkles, HeartPulse } from 'lucide-react';
import '../styles/addon-tabs.css';

// Logos de marca (no existen en lucide). Monocolor con currentColor para que
// hereden el estado activo/inactivo de la pestaña como el resto de iconos.
const GoogleG = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M21.35 11.1H12v3.8h5.35c-.5 2.5-2.62 3.9-5.35 3.9a5.8 5.8 0 1 1 0-11.6c1.48 0 2.82.55 3.86 1.45l2.84-2.84A9.77 9.77 0 0 0 12 2.2a9.8 9.8 0 1 0 0 19.6c5.66 0 9.4-3.98 9.4-9.58 0-.37-.02-.75-.05-1.12z" />
  </svg>
);

const MetaInfinity = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M6.9 6.5c1.9 0 3.4 1.5 5.1 4.2 1.7-2.7 3.2-4.2 5.1-4.2 2.9 0 4.9 2.9 4.9 6.1 0 3-1.4 4.9-3.6 4.9-1.9 0-3.2-1.4-5-4.4l-.4-.7-.4.7c-1.8 3-3.1 4.4-5 4.4-2.2 0-3.6-1.9-3.6-4.9 0-3.2 2-6.1 4.9-6.1zm10.1 2.1c-1.1 0-2.2 1.3-3.7 3.8 1.6 2.7 2.5 3.5 3.5 3.5s1.6-1 1.6-2.9c0-2.5-1.2-4.4-2.7-4.4h1.3zm-8.8 0c-1.5 0-2.7 1.9-2.7 4.4 0 1.9.6 2.9 1.6 2.9s1.9-.8 3.5-3.5c-1.5-2.5-2.6-3.8-3.7-3.8h1.3z" />
  </svg>
);

const TikTokNote = ({ size = 15, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.6 2h-3.4v13.4a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V9.1a6.4 6.4 0 1 0 5.4 6.3V8.6a8.1 8.1 0 0 0 4.7 1.5V6.7a4.7 4.7 0 0 1-4.7-4.7z" />
  </svg>
);

// Addons conocidos por la suite. El backend decide cuáles están activos; aquí
// solo se define cómo se pintan (etiqueta, ruta e icono).
const ADDONS = [
  { key: 'crawl',     label: 'Crawl SEO',  path: '/task/crawl',      icon: Globe },
  { key: 'gads',      label: 'Google Ads', path: '/task/gads-panel', icon: GoogleG },
  { key: 'meta',      label: 'Meta Ads',   path: '/task/meta',       icon: MetaInfinity },
  { key: 'tiktok',    label: 'TikTok Ads', path: '/task/tiktok',     icon: TikTokNote },
  { key: 'changelog', label: 'Changelog',  path: '/task/changelog',  icon: History },
  { key: 'seo',       label: 'SEO',        path: '/task/seo',        icon: Search },
  { key: 'creatives', label: 'Creatividades', path: '/task/creatives', icon: Megaphone },
  { key: 'aivis',     label: 'Visibilidad IA', path: '/task/aivis',    icon: Sparkles },
  { key: 'onesearch', label: 'OneSearch',   path: '/task/onesearch',  icon: HeartPulse },
];

// Deriva la pestaña activa de la ruta actual. Cualquier ruta que no sea de un
// addon (dashboard, detalle, crear, docs, config...) mantiene "Tareas" activa.
function activeKeyFromPath(pathname) {
  if (pathname.startsWith('/task/crawl')) return 'crawl';
  if (pathname.startsWith('/task/gads-panel')) return 'gads';
  if (pathname.startsWith('/task/meta')) return 'meta';
  if (pathname.startsWith('/task/tiktok')) return 'tiktok';
  if (pathname.startsWith('/task/changelog')) return 'changelog';
  if (pathname.startsWith('/task/seo')) return 'seo';
  if (pathname.startsWith('/task/creatives')) return 'creatives';
  if (pathname.startsWith('/task/aivis')) return 'aivis';
  if (pathname.startsWith('/task/onesearch')) return 'onesearch';
  return 'tasks';
}

// Ordena la lista de addons según el orden guardado del cliente (`order`, array
// de claves). Las claves presentes en `order` van primero en ese orden; las que
// falten conservan su orden por defecto (el del array ADDONS) al final, para que
// un addon nuevo aún sin ordenar nunca desaparezca del menú.
function orderAddons(list, order) {
  if (!Array.isArray(order) || order.length === 0) return list;
  const rank = new Map(order.map((k, i) => [k, i]));
  const END = order.length;
  return list
    .map((a, i) => ({ a, i }))
    .sort((x, y) => {
      const rx = rank.has(x.a.key) ? rank.get(x.a.key) : END + x.i;
      const ry = rank.has(y.a.key) ? rank.get(y.a.key) : END + y.i;
      return rx - ry;
    })
    .map((o) => o.a);
}

function AddonTabs({ addons, order }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Arrastre con ratón para desplazar la barra (efecto swiper en escritorio). En
  // táctil/pen NO intervenimos: el swipe nativo con momentum ya funciona y
  // duplicarlo pelearía con el scroll del navegador.
  const scrollerRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false });
  // Fades laterales: indican que hay más pestañas fuera de vista (la scrollbar
  // está oculta). Se recalculan al desplazar, al redimensionar y al cambiar el
  // número de pestañas activas.
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  const onPointerDown = (e) => {
    // Limpia el estado de un gesto anterior sea cual sea el tipo de puntero: evita
    // que un `moved` residual de un arrastre con ratón bloquee un tap táctil luego.
    drag.current.moved = false;
    drag.current.captured = false;
    if (e.pointerType && e.pointerType !== 'mouse') return; // en táctil/pen, swipe nativo
    const el = scrollerRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false, captured: false };
    // OJO: NO capturamos el puntero aquí. Hacerlo en cada pointerdown desviaba el
    // evento `click` del <button> de la pestaña hacia el <nav> con captura, y las
    // pestañas dejaban de responder al clic. Solo capturamos si arranca un arrastre
    // real (ver onPointerMove); un clic limpio no captura nunca.
  };
  const onPointerMove = (e) => {
    const el = scrollerRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 6 && !drag.current.moved) {
      drag.current.moved = true;
      // Ahora sí: el arrastre puede salirse de la barra (es estrecha), así seguimos
      // recibiendo move/up. Se libera en endDrag.
      try { el.setPointerCapture(e.pointerId); drag.current.captured = true; } catch (_) {}
    }
    if (drag.current.moved) {
      el.scrollLeft = drag.current.startLeft - dx;
      el.classList.add('is-dragging');
    }
  };
  const endDrag = (e) => {
    const el = scrollerRef.current;
    if (el) {
      el.classList.remove('is-dragging');
      if (drag.current.captured && e && e.pointerId != null) {
        try { el.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    }
    drag.current.down = false;
    drag.current.captured = false;
    // OJO: no reseteamos `moved` aquí; onClickCapture lo necesita justo después
    // del pointerup para cancelar el click que sigue a un arrastre.
  };
  // Si el puntero se arrastró, cancela el click de navegación de la pestaña para
  // que soltar el arrastre no cambie de módulo sin querer.
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const active = activeKeyFromPath(location.pathname);
  // addons ausente (null/undefined) = todos activos: es el default documentado
  // (columna/NULL = todos activos) y además cubre el dev local, donde el backend
  // de producción todavía no envía `addons`. Un objeto explícito (aunque sea {})
  // se respeta tal cual: una clave ausente = módulo desactivado.
  const effectiveAddons = addons == null ? { crawl: true, gads: true } : addons;
  const enabled = orderAddons(ADDONS.filter((a) => effectiveAddons[a.key]), order);

  // Recalcular los fades al montar, al redimensionar y cuando cambia el nº de
  // pestañas. (El scroll manual del arrastre dispara onScroll, que ya lo recalcula.)
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, enabled.length]);

  // Trae la pestaña activa a la vista al montar, al cambiar de ruta y cuando
  // llegan los addons del cliente (la config es asíncrona: la pestaña activa
  // puede montarse después del primer render). Sin `smooth` explícito: la barra
  // ya tiene scroll-behavior suave en CSS y la regla global de
  // prefers-reduced-motion lo anula cuando el usuario lo pide.
  const enabledKeys = enabled.map((a) => a.key).join(',');
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const tab = el.querySelector('.addon-tab.is-active');
    if (!tab) return;
    // Solo desplaza la barra (no la página): calcula el scrollLeft necesario
    // en lugar de scrollIntoView, que también movería el documento en vertical.
    const pad = 24; // deja ver el fade lateral
    const left = tab.offsetLeft - el.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left - pad < el.scrollLeft) {
      el.scrollLeft = Math.max(0, left - pad);
    } else if (right + pad > el.scrollLeft + el.clientWidth) {
      el.scrollLeft = right + pad - el.clientWidth;
    }
    updateEdges();
  }, [active, enabledKeys, updateEdges]);

  // Sin ningún addon activo la barra sobra: solo mostraría "Tareas" a secas
  // (ya estás en el tablero) y no hay ningún módulo al que saltar.
  if (enabled.length === 0) {
    return null;
  }

  return (
    <nav
      className={`addon-tabs${edges.left ? ' has-left' : ''}${edges.right ? ' has-right' : ''}`}
      aria-label="Módulos"
      ref={scrollerRef}
      onScroll={updateEdges}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
    >
      <button
        type="button"
        className={`addon-tab${active === 'tasks' ? ' is-active' : ''}`}
        aria-current={active === 'tasks' ? 'page' : undefined}
        onClick={() => navigate('/task')}
      >
        <ListChecks size={15} className="addon-tab__icon" />
        <span>Tareas</span>
      </button>

      {enabled.map((a) => {
        const Icon = a.icon;
        return (
          <button
            key={a.key}
            type="button"
            className={`addon-tab${active === a.key ? ' is-active' : ''}`}
            aria-current={active === a.key ? 'page' : undefined}
            onClick={() => navigate(a.path)}
          >
            <Icon size={15} className="addon-tab__icon" />
            <span>{a.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export default AddonTabs;
